import { recordArtifactCaptured, recordArtifactDeleted, recordArtifactRequested, } from '@streem/analytics';
import { StreemApiCreateSignedUploadUrlRequestType, StreemAPI } from '@streem/api';
import { StreemshotId, StreemshotRevisionId } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { Observable } from 'rxjs';
import { debounceTime, filter, map, mergeAll, mergeMap, scan, shareReplay, take, } from 'rxjs/operators';
import { fetchDownloadUrl } from '../../util/fetch_download_url';
import { logger } from '../../util/logger';
import { artifactTimedDeletion } from '../../util/rx/artifact_timed_deletion';
import { filterEmptyArray } from '../../util/rx/filter_empty_array';
import { firstFromGroup } from '../../util/rx/first_from_group';
import { accumulateAndMapWallEvents, filterByProperty, findInGroup, mapWithMetadata, } from '../wall/utils';
export class StreemshotController {
    constructor(config) {
        this.config = config;
        this.log = logger.extend('sdk:streemshot_controller');
        this.storageCache = {};
        this.requests = this.config.context?.localUserId
            ? this.config.roomWall.itemsByType.pipe(findInGroup('streemshotRequest'), accumulateAndMapWallEvents('streemshotRequest'), this.requestedOf(this.config.context.localUserId), map(requests => requests.reverse()))
            : new Observable();
        this.responses = this.config.roomWall.itemsByType.pipe(filter(g => g.key === 'streemshot' || g.key === 'streemshotRevision'), mergeAll(), scan((acc, cur) => {
            // First we need to make sure there is a key for each streemshot and the value is set to an empty array
            if (cur.itemType === 'streemshot' && !acc[cur.itemId])
                acc[cur.itemId] = [];
            if (cur.itemType === 'streemshotRevision' &&
                cur.type !== 'DELETED' &&
                cur.item?.streemshotRevision?.streemshotId &&
                !acc[cur.item.streemshotRevision?.streemshotId?.toString()])
                acc[cur.item.streemshotRevision?.streemshotId?.toString()] = [];
            if (cur.type === 'DELETED') {
                delete acc[new StreemshotId(cur.itemId).toString()];
                return acc;
            }
            // [STREEM-11612] Some old wall streemshots come in as semi-UUID (ss_9f882020-1aea-11eb-8acc-69ae09918942) instead of SID (ss_4r1zLgnsKQ1yhSbmAKgnsw)
            // Convert them all to SID standard to ensure compatability
            let streemshotId;
            switch (cur.itemType) {
                case 'streemshot':
                    streemshotId = new StreemshotId(cur.itemId);
                    break;
                case 'streemshotRevision':
                    invariant(cur.item.streemshotRevision?.streemshotId, 'Invalid streemshotRevision: could not find attached streemshotId');
                    streemshotId = new StreemshotId(cur.item.streemshotRevision.streemshotId);
                    break;
                default:
                    throw new Error(`Attempted to treat ${cur.itemType} as a streemshot`);
            }
            invariant(streemshotId, `Attempted to update a ${cur.itemType} without a streemshot ID`);
            switch (cur.itemType) {
                case 'streemshot': {
                    // [STREEM-11684] Android sometimes sends the revision before the associated streemshot
                    // If there exists a revision, only append new data (don't overwrite newer data)
                    const baseStreemshot = mapWithMetadata(cur.item, cur.itemType);
                    acc[streemshotId.toString()].push(baseStreemshot);
                    break;
                }
                case 'streemshotRevision': {
                    acc[streemshotId.toString()].push({
                        ...cur.item,
                        ...cur.item.streemshotRevision,
                    });
                    break;
                }
            }
            return acc;
        }, {}), debounceTime(250), // add a 250ms delay in case of an immediate revision (solves flashing image)
        mergeMap(async (acc) => {
            const streemshots = Object.values(acc);
            // map over streemshot wall items and use the `storageUrl` to fetch a presigned download url
            const streemshotDownloadPromises = await Promise.allSettled(streemshots.map(async (ss) => {
                let latestRevision;
                if (ss.length > 1) {
                    ss.sort((a, b) => (a.clientCreatedAt?.seconds?.toNumber() ?? 0) -
                        (b.clientCreatedAt?.seconds?.toNumber() ?? 0));
                    latestRevision = ss[ss.length - 1];
                    // show the timestamp for the original streemshot so that it appears corresponds accurately to the video
                    const originalIndex = ss.findIndex(item => !item.streemshotId);
                    latestRevision.clientCreatedAt = ss[originalIndex].clientCreatedAt;
                }
                else {
                    latestRevision = ss[0];
                }
                invariant(latestRevision.storageUrl, 'Invalid streemshot: could not find attached storageUrl');
                const cacheMiss = !this.storageCache[latestRevision.storageUrl];
                if (cacheMiss) {
                    this.storageCache[latestRevision.storageUrl] = await fetchDownloadUrl(this.config.context.roomId, latestRevision.storageUrl);
                }
                latestRevision.downloadUrl = this.storageCache[latestRevision.storageUrl];
                return latestRevision;
            }));
            const processedStreemshots = streemshotDownloadPromises
                .filter(p => p.status === 'fulfilled')
                .map((p) => p.value);
            return (processedStreemshots
                // Filter out errors
                .filter(ss => !!ss.downloadUrl)
                .sort((a, b) => (b.clientCreatedAt?.seconds?.toNumber() ?? 0) -
                (a.clientCreatedAt?.seconds?.toNumber() ?? 0)));
        }, 1), shareReplay(1));
        this.latestRequest = this.requests.pipe(filterEmptyArray(), firstFromGroup());
        this.latestResponse = this.responses.pipe(filterEmptyArray(), firstFromGroup());
    }
    /**
     * Adds a streemshot request to the room wall
     */
    requestStreemshot() {
        invariant(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'remoteUserId and remoteDeviceId are required for streemshot request');
        const artifactId = this.config.roomWall.addStreemshotRequest(this.config.context.remoteUserId, this.config.context.remoteDeviceId);
        recordArtifactRequested(artifactId);
        this.log.info(`Requesting streemshot from remoteUserId ${this.config.context.remoteUserId} on device ${this.config.context.remoteDeviceId}`, artifactId);
        return artifactId;
    }
    async uploadStreemshot(blob, id) {
        let signedUrl;
        try {
            signedUrl = await StreemAPI.uploads.createSignedUploadUrl({
                type: StreemApiCreateSignedUploadUrlRequestType.STREEMSHOT,
                group: this.config.context.roomId.toString(),
                sid: id?.toString(),
                filename: `frame_${Date.now()}.jpg`,
            });
        }
        catch (error) {
            this.log.error(`failed to create a signed upload url for request ${id}:`, error);
            throw error;
        }
        invariant(signedUrl.uploadUrl, 'An upload url must be defined to upload a streemshot');
        // Upload the streemshot.
        try {
            const headers = new Headers();
            headers.set('Content-Type', blob.type);
            headers.set('Content-Length', blob.size.toString());
            await fetch(signedUrl.uploadUrl, {
                headers: headers,
                method: 'PUT',
                mode: 'cors',
                body: blob,
            });
            this.log.info(`successfully uploaded streemshot for room ${this.config.context.roomId} in response to request ${id}`);
        }
        catch (error) {
            this.log.error(`failed to upload streemshot to ${signedUrl.uploadUrl}: `, error);
            throw error;
        }
        return signedUrl;
    }
    async saveEditedStreemshot(blob, id) {
        const newId = new StreemshotRevisionId();
        const signedUrl = await this.uploadStreemshot(blob, newId);
        // Add streemshotRevision wall item
        if (signedUrl) {
            const wallItemId = this.config.roomWall.addStreemshotRevision(newId, {
                storageUrl: signedUrl.storageUrl,
                downloadUrl: null,
                streemshotId: id,
                edited: true,
            });
            this.log.info(`added streemshot revision ${wallItemId} to wall`);
        }
    }
    revertEditedStreemshot(originalStreemshotId) {
        this.config.roomWall.itemsByType
            .pipe(findInGroup('streemshot'), accumulateAndMapWallEvents('streemshot'), filterByProperty('id', originalStreemshotId), take(1))
            .subscribe(streemshot => {
            const newId = new StreemshotRevisionId();
            const wallItemId = this.config.roomWall.addStreemshotRevision(newId, {
                storageUrl: streemshot[0].storageUrl,
                downloadUrl: null,
                streemshotId: streemshot[0].id,
            });
            this.log.info(`reverted streemshot revision and added new revision ${wallItemId} to wall`);
        });
    }
    async fulfillStreemshot(streemshotRequestId, streemshotId, payload, imageDimensions) {
        const signedUrl = await this.uploadStreemshot(payload, streemshotId);
        // Add streemshot wall item
        if (signedUrl) {
            const wallItemId = this.config.roomWall.addStreemshotResponse(streemshotId, {
                inResponseToWallItemId: streemshotRequestId.toString(),
                storageUrl: signedUrl.storageUrl,
                imageDimensions: imageDimensions,
                cameraIntrinsics: null,
                downloadUrl: null,
                pose: null,
                featurePoints: null,
            });
            try {
                recordArtifactCaptured(wallItemId);
            }
            catch (error) {
                // swallow errors
            }
            this.log.info(`added streemshot response ${wallItemId} to wall`);
        }
    }
    /**
     * Get a Streemshot by ID
     * @param id Streemshot ID
     */
    getStreemshot(id) {
        return this.responses.pipe(filterByProperty('id', id.toString()), firstFromGroup());
    }
    /**
     * Deletes a streemshot off of the backend
     * @param id The streemshot id to delete
     */
    async deleteStreemshot(id) {
        try {
            await StreemAPI.rooms.deleteStreemshot(this.config.context.roomId.toString(), id.toString());
            await artifactTimedDeletion(id.toString(), this.responses);
            recordArtifactDeleted({
                artifactSid: id.toString(),
                roomSid: this.config.context.roomId.toString(),
                streemshot: {},
            });
        }
        catch (e) {
            this.log.error('Failed to delete streemshot', id.toString());
        }
    }
    /**
     * Filters streemshot requests by who is requested to **provide** the streemshot
     * @param userId the user who should provide the streemshot
     */
    requestedOf(userId) {
        return map((requests) => requests.filter(request => request.requestedOfUserId === userId.toString()));
    }
}
//# sourceMappingURL=streemshot_controller.js.map